import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Loader2, User as UserIcon, Save, X } from "lucide-react";
import AppVersion from "@/components/AppVersion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(u) {
  if (!u) return null;
  return u.startsWith("http") ? u : `${BACKEND_URL}${u}`;
}

export default function Profile() {
  const { user, setUser, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  if (!user) return null;
  const dirty = name !== (user.name || "") || picture !== (user.picture || "");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("File harus berupa gambar");
    if (file.size > 5 * 1024 * 1024) return toast.error("Ukuran maksimal 5MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/me/upload-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPicture(data.url);
      toast.success("Foto siap. Klik Simpan untuk menerapkan.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Nama tidak boleh kosong");
    setSaving(true);
    try {
      const { data } = await api.put("/me/profile", { name: name.trim(), picture: picture || null });
      setUser(data);
      toast.success("Profil tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setName(user.name || "");
    setPicture(user.picture || "");
  };

  const avatarSrc = resolveUrl(picture);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-page">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#5C5868] hover:text-[#1A1422] mb-6">
        <ArrowLeft className="w-4 h-4" /> dashboard
      </Link>

      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#1A1422] mb-2">Profil Saya</h1>
      <p className="text-[#5C5868] mb-8">Atur foto profil & nama tampilan kamu.</p>

      <div className="card-surface p-6 sm:p-8">
        {/* Avatar */}
        <div className="flex items-center gap-6 mb-8">
          <div className="relative">
            {avatarSrc ? (
              <img src={avatarSrc} alt="" className="w-24 h-24 rounded-full object-cover border-4 border-[#F4F2F8]" data-testid="profile-avatar" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-[#F4F2F8] flex items-center justify-center" data-testid="profile-avatar-empty">
                <UserIcon className="w-10 h-10 text-[#8B8794]" />
              </div>
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="profile-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-outline text-sm"
              data-testid="profile-upload-button"
            >
              <Upload className="w-4 h-4" /> {avatarSrc ? "Ganti foto" : "Upload foto"}
            </button>
            {picture && (
              <button
                type="button"
                onClick={() => setPicture("")}
                className="btn-ghost text-sm text-[#EF4444] block"
                data-testid="profile-remove-photo"
              >
                <X className="w-3.5 h-3.5 inline" /> Hapus foto
              </button>
            )}
            <p className="text-xs text-[#8B8794]">JPG, PNG, WEBP — maks 5MB</p>
          </div>
        </div>

        {/* Name */}
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-[#5C5868] mb-1.5 block">Nama tampilan</span>
            <input
              className="input-field"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nama lengkap kamu"
              maxLength={100}
              data-testid="profile-name-input"
            />
          </label>

          <label className="block">
            <span className="text-xs text-[#5C5868] mb-1.5 block">Email</span>
            <input className="input-field bg-[#F4F2F8]" value={user.email} disabled data-testid="profile-email-input" />
            <span className="text-[10px] text-[#8B8794] mt-1 block">Email diambil dari akun Google kamu, tidak bisa diubah.</span>
          </label>

          {user.role === "admin" && (
            <div className="bg-[#6B21A8]/5 border border-[#6B21A8]/20 rounded-xl p-3">
              <span className="text-xs font-semibold text-[#6B21A8]">⚡ AKUN ADMIN</span>
              <p className="text-xs text-[#5C5868] mt-0.5">Kamu punya akses penuh ke Admin Panel.</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-8 pt-6 border-t border-[#E8E5EE]">
          <button
            onClick={save}
            disabled={!dirty || saving || uploading}
            className="btn-primary flex-1 justify-center"
            data-testid="profile-save-button"
          >
            <Save className="w-4 h-4" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
          {dirty && (
            <button onClick={reset} disabled={saving} className="btn-ghost" data-testid="profile-reset-button">
              Batal
            </button>
          )}
        </div>
      </div>

      {/* Versi & Pembaruan */}
      <div className="mt-6">
        <h2 className="font-heading text-lg font-bold text-[#1A1422] mb-3">Versi &amp; Pembaruan</h2>
        <AppVersion />
      </div>
    </div>
  );
}
